<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import router from '@/router'
import { useDjangoAuthStore } from '@/stores/authDjango'
import { useProjectStore } from '@/stores/project'
import { useAuthorizationStore } from '@/stores/authorization'
import { useProjectUserStore } from '@/stores/projectUser'
import type { Project } from '@/stores/type/project.type'
import { toDateInputValue, toDisplayDate } from '@/utils/projectDate'
import {
  CreateOrUpdateDialog,
  DeleteDialog,
  ProjectIndex,
  WorkOrderField
} from '@haskoning/vue-component-library'

const authorizationStore = useAuthorizationStore()
const projectStore = useProjectStore()
const projectUserStore = useProjectUserStore()
const authStore = useDjangoAuthStore()

const { user } = storeToRefs(authStore)
const {
  items,
  currentItem,
  createUpdateDialog,
  deleteDialog,
  deleteItem,
  fetchLoading: projectFetchLoading,
  writeLoading,
  deleteLoading,
  fieldsFiltered
} = storeToRefs(projectStore)

const startDateMenu = ref(false)
const startDateActivatorRef = ref<HTMLElement | null>(null)
const startDateMenuWidth = ref<number>()

const getLocalStartedDate = () => toDateInputValue(new Date())

const createEmptyProject = (overrides: Partial<Project> = {}): Project => ({
  id: 0,
  work_order: '',
  name: '',
  location: '',
  started_date: null,
  description: '',
  lifetime: 0,
  client: '',
  date_created: '',
  date_modified: '',
  ...overrides
})

const toDialogStartedDate = (value?: string | Date | null) => {
  return toDateInputValue(value) || getLocalStartedDate()
}

const onStartDateSelected = (value: string | Date | null) => {
  if (!currentItem.value) return
  currentItem.value.started_date = toDateInputValue(value)
  if (currentItem.value.started_date) {
    startDateMenu.value = false
  }
}

const syncStartDateMenuWidth = () => {
  if (!startDateActivatorRef.value) return
  startDateMenuWidth.value = Math.ceil(startDateActivatorRef.value.getBoundingClientRect().width)
}

const navigateToProject = (projectID: number) => {
  const projectNavigated = items.value.find((project: Project) => project.id === projectID)
  projectStore.setCurrentItem(projectNavigated)
  router.push({ name: 'project-detail', params: { id: projectID } })
}

const handleAddProject = async () => {
  if (!projectStore.fieldsList.length) {
    await projectStore.fetchOptions()
  }
  projectStore.setCurrentItem(createEmptyProject({ started_date: getLocalStartedDate() }))
  projectStore.openCreateUpdateDialog()
}

const handleEditProject = async (item: Project) => {
  if (!projectStore.fieldsList.length) {
    await projectStore.fetchOptions()
  }
  projectStore.setCurrentItem({
    ...item,
    started_date: toDialogStartedDate(item.started_date)
  })
  projectStore.openCreateUpdateDialog()
}

const handleDeleteProject = (item: Project) => {
  projectStore.setDeleteItem(item)
  projectStore.openDeleteDialog()
}

const codeField = computed(() => {
  return projectStore.fieldsList.find((field) => field.parameter === 'work_order')
})

const startDateField = computed(() => {
  return projectStore.fieldsList.find((field) => field.parameter === 'started_date')
})

watch(createUpdateDialog, async () => {
  if (createUpdateDialog.value && currentItem.value) {
    currentItem.value.started_date = toDialogStartedDate(currentItem.value.started_date)
  }
  if (createUpdateDialog.value) {
    await nextTick()
    syncStartDateMenuWidth()
  }
  if (!createUpdateDialog.value) {
    projectStore.setCurrentItem(createEmptyProject())
  }
})

watch(startDateMenu, async (opened) => {
  if (!opened) return
  await nextTick()
  syncStartDateMenuWidth()
})

watch(deleteDialog, () => {
  if (!deleteDialog.value) {
    projectStore.setDeleteItem(createEmptyProject())
  }
})

onMounted(async () => {
  if (user.value) {
    authorizationStore.setCurrentRole([user.value.role])
  }
  await projectStore.fetchOptions()
  if (!items.value?.length) {
    await projectStore.fetchItems()
  }
})

watch(
  () => items.value.map((item) => item.id).join(','),
  async (ids, previousIds) => {
    if (!ids || ids === previousIds) return
    await projectStore.servicePermissionItems()
  },
  { immediate: true }
)

onUnmounted(() => {
  projectStore.resetAll()
  projectUserStore.reset()
  authorizationStore.resetState()
})
</script>

<template>
  <div class="fill-height">
    <ProjectIndex
      class="project-header-image"
      title="SIM"
      :items="items"
      :loading="projectFetchLoading"
      :get-change-permission="projectStore.getChangePermission"
      :get-delete-permission="projectStore.getDeletePermission"
      @navigate-to-project="navigateToProject"
      @add-item="handleAddProject"
      @edit-item="handleEditProject"
      @delete-item="handleDeleteProject"
    >
      <template #extra-fields="{ item }">
        <v-chip
          class="mr-3"
          label
          prepend-icon="mdi-calendar"
          size="x-small"
          v-if="item.started_date"
        >
          {{ toDisplayDate(item.started_date) }}
        </v-chip>
      </template>
    </ProjectIndex>
    <CreateOrUpdateDialog
      v-if="currentItem"
      name="project"
      v-model="createUpdateDialog"
      v-model:item="currentItem"
      :loading="writeLoading"
      :fields="fieldsFiltered"
      @submit="projectStore.postOrUpdate($event)"
    >
      <WorkOrderField
        v-if="codeField"
        :field="codeField"
        validate-code
        v-model="currentItem"
      ></WorkOrderField>

      <v-menu
        v-if="startDateField"
        v-model="startDateMenu"
        :close-on-content-click="false"
        contained
        location="bottom start"
        :z-index="2600"
        :min-width="startDateMenuWidth"
        :max-width="startDateMenuWidth"
        content-class="project-start-date-menu-content"
      >
        <template #activator="{ props: dateActivatorProps }">
          <div ref="startDateActivatorRef">
            <v-text-field
              v-bind="dateActivatorProps"
              label="Start date"
              prepend-inner-icon="mdi-calendar"
              :model-value="toDisplayDate(currentItem?.started_date)"
              readonly
              color="primary"
              density="comfortable"
              hide-details
              class="mb-4"
            />
          </div>
        </template>
        <v-date-picker
          :model-value="toDateInputValue(currentItem?.started_date)"
          @update:model-value="onStartDateSelected"
          color="primary"
          hide-header
          rounded="0"
          elevation="0"
          class="project-start-date-picker"
        />
      </v-menu>
    </CreateOrUpdateDialog>

    <DeleteDialog
      v-if="deleteItem"
      v-model="deleteDialog"
      :loading="deleteLoading"
      :name="deleteItem?.name"
      :item="deleteItem"
      @set-delete-item="deleteItem = $event"
      @delete="projectStore.delete(deleteItem)"
    ></DeleteDialog>
  </div>
</template>

<style scoped>
:deep(.v-table__wrapper table) {
  border: 1px solid lightgrey;
}

:deep(.project-start-date-menu-content) {
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0 !important;
  background: transparent !important;
}

:deep(.project-start-date-picker.v-picker) {
  width: 100%;
  min-width: 100%;
  border-radius: 0 !important;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
  background: rgb(var(--v-theme-surface-light));
}

:deep(.project-start-date-picker .v-date-picker-controls) {
  padding-inline: 12px;
}

:deep(.project-start-date-picker .v-date-picker-month) {
  padding: 0 10px 10px;
}

:deep(.project-start-date-picker .v-btn) {
  font-size: 0.875rem;
  font-weight: 400 !important;
}

:deep(.project-start-date-picker .v-date-picker-controls__month-btn .v-btn__content) {
  font-weight: 400 !important;
  font-size: 0.95rem !important;
}

:deep(.project-start-date-picker .v-date-picker-month__weekday) {
  font-weight: 400 !important;
  font-size: 0.8rem !important;
}

:deep(.project-start-date-picker .v-date-picker-month__day-btn) {
  font-weight: 400 !important;
}

:deep(.project-start-date-picker .v-date-picker-month__day-btn .v-btn__content) {
  font-size: 0.9rem !important;
}

:deep(.project-start-date-picker .v-date-picker-month__day .v-btn),
:deep(.project-start-date-picker .v-date-picker-month__day--selected .v-btn),
:deep(.project-start-date-picker .v-date-picker-month__day--today .v-btn),
:deep(.project-start-date-picker .v-date-picker-month__day .v-btn .v-btn__content),
:deep(.project-start-date-picker .v-date-picker-month__day--selected .v-btn .v-btn__content),
:deep(.project-start-date-picker .v-date-picker-month__day--today .v-btn .v-btn__content) {
  font-weight: 400 !important;
}
</style>
